// src/services/shelterChangeSummary.ts
// -------------------------------------
// PURE: which portal-owned Unleashed Customer fields changed between the loaded DFV
// Service and the edited form. Used to stamp a customer push job's payload.changes so
// the Push List inspect modal can show "Address changed from X to Y", "Warehouse
// changed", etc. Only the fields the portal PUSHES to Unleashed are compared
// (customerMap): name / customer type / sales order group / taxable / stop credit / ABN
// / default warehouse / physical + postal address. Notes + Reminder are read-only
// (Unleashed → portal, never pushed) so they're excluded. Returns [] when nothing
// pushed-relevant changed. No React / no DB — unit-tests cleanly.

export interface ShelterChange {
  label: string;
  from: string;
  to: string;
}

type Rec = Record<string, unknown>;

const str = (v: unknown): string => String(v ?? "").trim();
const yesNo = (v: boolean): string => (v ? "Yes" : "No");
const dash = (s: string): string => (s === "" ? "—" : s);

// Join an address's parts (matches how the push maps them: locality = *_suburb_city).
const joinAddr = (r: Rec, prefix: string): string =>
  [
    r[`${prefix}_line1`],
    r[`${prefix}_line2`],
    r[`${prefix}_suburb_city`],
    r[`${prefix}_state`],
    r[`${prefix}_postcode`],
  ]
    .map(str)
    .filter(Boolean)
    .join(", ");

export function computeShelterChanges(
  oldS: Rec | null | undefined,
  newF: Rec | null | undefined
): ShelterChange[] {
  const o = oldS || {};
  const n = newF || {};
  const out: ShelterChange[] = [];

  const scalar = (label: string, key: string) => {
    const from = str(o[key]);
    const to = str(n[key]);
    if (from !== to) out.push({ label, from: dash(from), to: dash(to) });
  };
  scalar("Name", "shelter_name");
  scalar("Customer type", "customer_type");
  scalar("Sales order group", "sales_group");
  scalar("ABN", "abn");
  scalar("Default warehouse", "default_warehouse");

  // Booleans, defaulted like buildShelterPatch (taxable defaults true, stop_credit false).
  const oTax = o.taxable !== false;
  const nTax = n.taxable !== false;
  if (oTax !== nTax) out.push({ label: "Taxable", from: yesNo(oTax), to: yesNo(nTax) });
  const oStop = o.stop_credit === true;
  const nStop = n.stop_credit === true;
  if (oStop !== nStop) out.push({ label: "Stop credit", from: yesNo(oStop), to: yesNo(nStop) });

  const oPhys = joinAddr(o, "physical");
  const nPhys = joinAddr(n, "physical");
  if (oPhys !== nPhys) out.push({ label: "Physical address", from: dash(oPhys), to: dash(nPhys) });
  const oPost = joinAddr(o, "postal");
  const nPost = joinAddr(n, "postal");
  if (oPost !== nPost) out.push({ label: "Postal address", from: dash(oPost), to: dash(nPost) });

  return out;
}
